import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';

@Component({
  tag: 'lar-door',
  styleUrl: 'door.scss',
  shadow: true
})
export class Door {
  @Element() el!: HTMLElement;
  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'door';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Disable quck actions
   */
  @Prop() disableQuickActions: boolean = false;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() doorType: string = 'buzz';
  @State() loading: boolean = true;
  @State() isOpen?: boolean;
  // state for indicating buzzer door open state
  @State() buzzingState: boolean = false;
  private node!: HTMLLarNodeElement;

  /**
   * Larva input message
   */
  @Method()
  async input(data: any) {
    this.isOpen = data.state || false;
    if (this.buzzingState && !this.isOpen) {
      this.buzzingState = false;
    }
    if (data.doorType) {
      this.doorType = data.doorType;
    }
    this.loading = false;
  }
  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit({ command: 'fullstate' });
  }

  handleChange() {
    if (this.loading || this.buzzingState) {
      return this.node.error('invalid door state');
    }
    this.loading = true;
    const cmd = {
      command:
        this.doorType === 'buzz' ? 'buzz' : this.isOpen ? 'lock' : 'unlock'
    };
    if (this.doorType === 'buzz') {
      this.buzzingState = true;
    }
    return this.output.emit(cmd);
  }

  render() {
    const componentProps = {
      onClick: () => this.handleChange(),
      value: this.doorType === 'buzz' ? 'door.buzzOpen' : this.isOpen ? 'door.lock' : 'door.unlock',
      disabled: this.loading || this.buzzingState === true,
      color: this.colorInputs || this.color
    };
    return [
      <lar-node
        value={this.isOpen ? 'door.unlocked' : 'door.locked'}
        colorIconSmall={this.allowIndicationAutoColoring && !this.loading ? (this.isOpen? 'danger': 'success') : this.colorIconSmall}
        hideTitles={this.hideTitles}
        iconSmall={this.isOpen ? 'unlock' : 'lock'}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        nodeSize={this.nodeSize}
        log={this.log}
        loading={this.loading}
        component="lar-door-content"
        componentProps={componentProps}
      >
      {this.disableQuickActions === false &&
          <lar-button-push
            size="small"
            slot="titles"
            color={this.colorInputs || this.color}
            disabled={this.loading || this.buzzingState === true}
            onClick={() => this.handleChange()}
            />
        }
        <slot />
      </lar-node>
    ];
  }
}
